import { useCallback, useEffect, useState } from 'react'

import {
  createRepairOrderFile,
  deleteRepairOrderFile,
  updateRepairOrder,
} from '@/services/repairOrders.service'
import { toast } from '@/utils/helpers'

import { blankEdit, DISPOSITION, hasChanges, savesInPlace } from './queue'

/**
 * Save one row: files first, then the status and dealer-closed flag in one PATCH.
 * Throws with the API's message so Save all can report which rows failed and why.
 *
 * Each file change reports as soon as it succeeds, so the caller can drop it from the
 * staged edit. If the status save then fails, a retry repeats only what is left: it must
 * not delete a file twice or upload a duplicate (PR review, WARP-959).
 */
export const saveRow = async (row, edit, { onFileDone = () => {} } = {}) => {
  const dealerId = row.dealer?.id || row.dealer

  await Promise.all(
    edit.filesToDelete.map((fileId) =>
      deleteRepairOrderFile(dealerId, row.id, fileId).then(() => onFileDone({ deleted: fileId })),
    ),
  )
  await Promise.all(
    edit.newFiles.map((file) => {
      const formData = new FormData()
      formData.append('file', file.raw)
      formData.append('display_name', file.raw.name)
      return createRepairOrderFile(dealerId, row.id, formData).then(() =>
        onFileDone({ uploaded: file }),
      )
    }),
  )

  const payload = {}
  if (edit.disposition) payload.status = edit.disposition
  if (edit.dealerClosed !== Boolean(row.dealerClosed)) payload.dealerClosed = edit.dealerClosed
  if (Object.keys(payload).length === 0) return

  let message = null
  const response = await updateRepairOrder(dealerId, row.id, payload, undefined, (err) => {
    message = err
  })
  if (!response) throw new Error(message || `RO ${row.roNumber} could not be saved.`)
}

/**
 * Edits staged on table rows, saved by a row's Save or by Save all (WARP-951, WARP-966).
 * The Work Queue and the dashboard's RO list share it, so a row works the same in both:
 * a status, the dealer-closed flag, and attachments, then one explicit Save. A wrong
 * click must not stick (Michelle, 2026-09-17).
 *
 * `resetKey` drops everything staged when it changes: another dealer, or another list.
 * `onSaved({ saved, failed })` runs after each commit, with the rows that saved.
 */
const useRowEdits = ({ resetKey, onSaved = () => {} }) => {
  const [edits, setEdits] = useState({})
  const [saving, setSaving] = useState({})

  useEffect(() => {
    setEdits({})
    setSaving({})
  }, [resetKey])

  const editFor = useCallback((row) => edits[row.id] || blankEdit(row), [edits])

  const updateEdit = useCallback((row, patch) => {
    setEdits((prev) => ({
      ...prev,
      // The row's saved flag rides along, so the unsaved-work check below can tell a
      // flag flipped and flipped back from a real change without the row in hand.
      [row.id]: {
        ...(prev[row.id] || blankEdit(row)),
        savedDealerClosed: Boolean(row.dealerClosed),
        ...patch,
      },
    }))
  }, [])

  const dropEdits = useCallback((ids) => {
    setEdits((prev) => {
      const next = { ...prev }
      ids.forEach((id) => delete next[id])
      return next
    })
  }, [])

  const commit = async (targets) => {
    // The edits as they stand now; the file callbacks below trim the live copy as each
    // file lands, and the caller needs to know what each row was saved with.
    const committed = Object.fromEntries(targets.map((row) => [row.id, editFor(row)]))

    const results = await Promise.allSettled(
      targets.map(async (row) => {
        setSaving((prev) => ({ ...prev, [row.id]: true }))
        try {
          await saveRow(row, committed[row.id], {
            onFileDone: ({ deleted, uploaded }) =>
              setEdits((prev) => {
                const edit = prev[row.id]
                if (!edit) return prev
                return {
                  ...prev,
                  [row.id]: {
                    ...edit,
                    filesToDelete: edit.filesToDelete.filter((id) => id !== deleted),
                    newFiles: edit.newFiles.filter((file) => file !== uploaded),
                  },
                }
              }),
          })
        } finally {
          setSaving((prev) => ({ ...prev, [row.id]: false }))
        }
      }),
    )

    const saved = targets.filter((_, index) => results[index].status === 'fulfilled')
    const failed = results.filter((result) => result.status === 'rejected')
    dropEdits(saved.map((row) => row.id))

    if (saved.length) {
      toast(`${saved.length} repair order${saved.length === 1 ? '' : 's'} saved.`, 'success')
    }
    failed.forEach((result) =>
      toast(result.reason?.message || 'A row could not be saved.', 'error'),
    )

    onSaved({ saved: saved.map((row) => ({ row, edit: committed[row.id] })), failed })
  }

  /** The rows a Save all would commit: every changed row the row's own Save can save. */
  const rowsToSave = (rows) => rows.filter((row) => savesInPlace(row, edits[row.id]))

  // Outstanding List waits on its form, which saves the RO itself, so it is not unsaved
  // work of the row's.
  const hasUnsaved = Object.values(edits).some(
    (edit) =>
      edit.disposition !== DISPOSITION.OUTSTANDING &&
      hasChanges({ dealerClosed: edit.savedDealerClosed }, edit),
  )

  // Unsaved work is lost on reload or tab close; say so first.
  useEffect(() => {
    if (!hasUnsaved) return undefined
    const warn = (event) => {
      event.preventDefault()
      // eslint-disable-next-line no-param-reassign
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [hasUnsaved])

  return {
    commit,
    dropEdits,
    editFor,
    edits,
    hasUnsaved,
    rowsToSave,
    saving,
    setEdits,
    updateEdit,
  }
}

export default useRowEdits
